import { useCallback, useContext } from "react";
import { IMarketOutcomeFeedResponse } from "@finbackoffice/clientbff-client";
import { EventStatus, MarketOutcomeStatus, MarketStatus } from "@finbackoffice/enums";
import { BetSlipActionsContext, MarketUpdatesContext } from "contexts";

type UseBetSlipOutcomeHandlerParams = {
    gameId: number;
    homeName: string;
    awayName: string;
    gameStatus: EventStatus;
};

/**
 * Hook that provides a callback for handling outcome clicks in the bet slip.
 * Manages adding/removing bet items and subscribing/unsubscribing to market updates.
 *
 * @param params - Configuration object containing game and team information
 * @returns Callback function to handle outcome clicks with flexible signature for different market types
 */
export const useBetSlipOutcomeHandler = ({
    gameId,
    homeName,
    awayName,
    gameStatus,
}: UseBetSlipOutcomeHandlerParams) => {
    const { addBetItem, removeBetItem, isOutcomeSelected } = useContext(BetSlipActionsContext);
    const { subscribeMarket, unsubscribeMarket } = useContext(MarketUpdatesContext);

    return useCallback(
        (
            marketId: number | null,
            outcome: IMarketOutcomeFeedResponse | null,
            marketName?: string | null,
            marketStatus?: MarketStatus,
        ) => {
            if (marketId && marketStatus && marketName && outcome) {
                if (!isOutcomeSelected(outcome.id)) {
                    addBetItem({
                        gameId,
                        marketId,
                        outcomeId: outcome.id,
                        team1Name: homeName,
                        team2Name: awayName,
                        marketName,
                        marketStatus,
                        eventStatus: gameStatus,
                        outcomeName: outcome?.name || "",
                        outcomeValue: outcome.odds,
                        outcomeStatus: MarketOutcomeStatus.Active,
                        outcomeInitValue: outcome.odds,
                        stake: "",
                        loading: false,
                        accepted: false,
                    });
                    subscribeMarket(gameId, marketId, outcome.id);
                } else {
                    removeBetItem(outcome.id);
                    unsubscribeMarket(outcome.id);
                }
            }
        },
        [
            gameId,
            homeName,
            awayName,
            gameStatus,
            isOutcomeSelected,
            addBetItem,
            subscribeMarket,
            removeBetItem,
            unsubscribeMarket,
        ],
    );
};
